import { useState } from 'react';
import { useNotifiche } from '../contexts/NotificheContext';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Bell, Check, CheckCheck, Trash2, Package, AlertTriangle } from 'lucide-react';

export default function NotificheDropdown() {
  const { notifiche, countNonLette, segnaLetta, segnaTutteLette, eliminaNotifica } = useNotifiche();
  const [open, setOpen] = useState(false);

  const getIcon = (tipo) => {
    switch (tipo) {
      case 'scorta_minima':
        return <Package className="h-4 w-4 text-warning" />;
      case 'scadenza_verifica':
      case 'scadenza_abbonamento':
        return <AlertTriangle className="h-4 w-4 text-urgent" />;
      default:
        return <Bell className="h-4 w-4" />;
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Adesso';
    if (minutes < 60) return `${minutes} min fa`;
    if (hours < 24) return `${hours} ore fa`;
    if (days < 7) return `${days} giorni fa`;
    return date.toLocaleDateString('it-IT');
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button 
          variant="ghost" 
          size="icon" 
          className="relative rounded-none"
          data-testid="notifiche-btn"
        >
          <Bell className="h-5 w-5" />
          {countNonLette > 0 && (
            <Badge 
              className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs rounded-full bg-urgent text-white border-0"
            >
              {countNonLette > 9 ? '9+' : countNonLette}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent 
        className="w-80 p-0 rounded-none" 
        align="end"
        data-testid="notifiche-dropdown"
      >
        <div className="flex items-center justify-between p-3 border-b border-border">
          <h3 className="font-mono text-sm uppercase tracking-wider font-bold">Notifiche</h3>
          {countNonLette > 0 && (
            <Button 
              variant="ghost" 
              size="sm" 
              className="h-7 text-xs rounded-none"
              onClick={segnaTutteLette}
            >
              <CheckCheck className="h-3 w-3 mr-1" />
              Leggi tutte
            </Button>
          )}
        </div>

        <ScrollArea className="h-80">
          {notifiche.length === 0 ? (
            <div className="p-6 text-center text-muted-foreground">
              <Bell className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">Nessuna notifica</p>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {notifiche.map((notifica) => (
                <div 
                  key={notifica.id} 
                  className={`p-3 hover:bg-muted/50 transition-colors ${!notifica.letta ? 'bg-primary/5' : ''}`}
                >
                  <div className="flex gap-3">
                    <div className="mt-0.5">
                      {getIcon(notifica.tipo)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p className={`text-sm ${!notifica.letta ? 'font-semibold' : ''}`}>
                          {notifica.titolo}
                        </p>
                        {!notifica.letta && (
                          <div className="w-2 h-2 rounded-full bg-primary flex-shrink-0 mt-1.5" />
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                        {notifica.messaggio}
                      </p>
                      <div className="flex items-center justify-between mt-2">
                        <span className="text-xs text-muted-foreground">
                          {formatDate(notifica.data)}
                        </span>
                        <div className="flex gap-1">
                          {!notifica.letta && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6 rounded-none"
                              onClick={() => segnaLetta(notifica.id)}
                              title="Segna come letta"
                            >
                              <Check className="h-3 w-3" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 rounded-none text-destructive"
                            onClick={() => eliminaNotifica(notifica.id)}
                            title="Elimina"
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}
